// React component that displays the tab content for the shared link tabs

import React from 'react';
import { TabPanel } from '../../../components/TwTabPanel/TwTabPanel';
import TripPublicDetail from '../TripPublicDetail/TripPublicDetail';
import { CLIENT_TYPE, SharedTokenResponse } from '../../../redux/api/orders/orders.model';
import FullPublicRouteDetail from '../FullPublicRouteDetail/FullPublicRouteDetail';
import { TripStop } from '../../../redux/api/trips/trips.model';
import LoadUtils from '../../../utils/loadUtils';
import { LOAD_TYPE } from '../../../redux/api/load/load.model';
import { ClientResponse } from '../../../redux/api/customer/customer.model';

interface SharedLinkTabContentProps {
  sharedTokenResponse: SharedTokenResponse;
  currentTab: number;
  enableLtl: boolean;
  refetchShareLinkData: () => Promise<void>;
}

const getDriverLinkTabContents = (
  sharedTokenResponse: SharedTokenResponse | null,
  currentTab: number,
  enableLtl: boolean,
  refetchShareLinkData: () => Promise<void>
): JSX.Element[] => {
  const trip = sharedTokenResponse?.trip;
  const stops: TripStop[] | undefined = trip?.stops.map((stop, index) => ({
    ...stop,
    sort_order: index + 1
  }));
  if (!trip || !stops) return [];
  const tenant = sharedTokenResponse?.tenant;
  const driver = sharedTokenResponse?.driver;

  // if split order, then calculate the starting
  const totalStopsInPreviousTrips =
    trip.orders &&
    trip.orders.length == 1 &&
    trip.orders[0].order_type === LOAD_TYPE.SEGMENT &&
    trip.orders[0].full_route_stop_overview
      ? LoadUtils.getPreviousStopsCountForTrip(trip.orders[0].full_route_stop_overview, trip.id)
      : 0;

  const orderToUse = enableLtl
    ? null
    : trip.orders && trip.orders.length > 0
    ? trip.orders[0]
    : null;

  // customer name to show on trip tab, for FTL should be same as order
  // for ltl should be the customers on the orders associated
  // we want to show a name and '+n' if there are more than one customer.

  let customers: ClientResponse[] = [];

  if (trip.orders && trip.orders.length > 0) {
    trip.orders.forEach((order) => {
      if (order.raw_customer) {
        customers.push(order.raw_customer);
      }
    });
  }

  let primaryClient = null;
  let customersCount = undefined;
  if (customers.length > 0) {
    customers = [...new Set(customers)];
    primaryClient = customers[0];
    customersCount = customers.length;
  }

  const items: JSX.Element[] = [
    <TabPanel id={`tw-segments-panel-${0}`} value={currentTab} key={0} index={0}>
      <TripPublicDetail
        trip={trip}
        order={orderToUse}
        tenant={tenant ?? undefined}
        primaryClient={primaryClient}
        driver={sharedTokenResponse?.driver ?? undefined}
        isDriverAssignedToTrip={sharedTokenResponse.isDriverAssignedToTrip ?? false}
        reloadData={refetchShareLinkData}
        segmentOrder={trip.sort_order}
        stops={stops}
        stopStartOrder={totalStopsInPreviousTrips || 1}
        customersCount={customersCount}
      />
    </TabPanel>
  ];

  trip.orders?.map((order, index) => {
    const primaryClient =
      order.primary_client === CLIENT_TYPE.BROKER ? order.broker : order.shipper;
    items.push(
      <TabPanel
        id={`tw-segments-panel-${index + 1}`}
        value={currentTab}
        key={index + 1}
        index={index + 1}
      >
        <FullPublicRouteDetail
          order={order}
          primaryClient={primaryClient}
          tenant={tenant}
          reloadData={refetchShareLinkData}
          driverId={driver ? driver.id : undefined}
          isDriverAssignedToTrip={sharedTokenResponse?.isDriverAssignedToTrip ?? false}
          trip={trip}
        />
      </TabPanel>
    );
  });

  return items;
};

const getPublicLinkTabContents = (
  sharedTokenResponse: SharedTokenResponse | null,
  currentTab: number,
  enableLtl: boolean,
  refetchShareLinkData: () => Promise<void>
): JSX.Element[] => {
  const order = sharedTokenResponse?.order;

  if (!order) return [];

  const primaryClient = order.raw_customer || null;

  const items: JSX.Element[] = [
    <TabPanel id={`tw-segments-panel-${0}`} value={currentTab} key={0} index={0}>
      <FullPublicRouteDetail
        order={order}
        primaryClient={primaryClient}
        tenant={sharedTokenResponse?.tenant}
        reloadData={refetchShareLinkData}
        isDriverAssignedToTrip={false}
      />
    </TabPanel>
  ];

  order.trips?.map((trip, index) => {
    items.push(
      <TabPanel
        id={`tw-segments-panel-${index + 1}`}
        value={currentTab}
        key={index + 1}
        index={index + 1}
      >
        <TripPublicDetail
          trip={trip}
          order={order}
          tenant={sharedTokenResponse?.tenant}
          primaryClient={enableLtl ? null : primaryClient}
          driver={sharedTokenResponse?.driver || undefined}
          isDriverAssignedToTrip={false}
          reloadData={refetchShareLinkData}
          segmentOrder={trip.sort_order}
          stops={trip.stops}
          stopStartOrder={LoadUtils.getPreviousStopsCount(order.trips, currentTab - 1) || 1}
        />
      </TabPanel>
    );
  });

  return items;
};

const SharedLinkTabContent: React.FC<SharedLinkTabContentProps> = ({
  sharedTokenResponse,
  currentTab,
  enableLtl,
  refetchShareLinkData
}) => {
  return (
    <>
      {sharedTokenResponse.driver
        ? getDriverLinkTabContents(sharedTokenResponse, currentTab, enableLtl, refetchShareLinkData)
        : getPublicLinkTabContents(
            sharedTokenResponse,
            currentTab,
            enableLtl,
            refetchShareLinkData
          )}
    </>
  );
};

export default SharedLinkTabContent;
